import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Modal from 'react-modal';
import { eventsApi } from '../../../api/adminApi';
import {
  eventId,
  eventDescription,
  eventDateInputValue,
  formatEventDateShort,
  todayLocalISODate,
} from './eventHelpers';
import './EventView.css';

function EventView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [editOpen, setEditOpen] = useState(false);
  const [formTitle, setFormTitle] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formEventDate, setFormEventDate] = useState('');
  const [formSaving, setFormSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const loadOne = (opts = {}) => {
    const silent = Boolean(opts.silent);
    if (!id) return Promise.resolve();
    if (!silent) setLoading(true);
    setError('');
    return eventsApi
      .getOne(id)
      .then((data) => setItem(data))
      .catch((err) => setError(err.userMessage || 'Failed to load event'))
      .finally(() => {
        if (!silent) setLoading(false);
      });
  };

  useEffect(() => {
    loadOne();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const openEdit = () => {
    if (!item) return;
    setFormTitle(item.title ?? '');
    setFormDescription(eventDescription(item));
    setFormEventDate(eventDateInputValue(item) || todayLocalISODate());
    setFormError('');
    setEditOpen(true);
  };

  const closeEdit = () => {
    setEditOpen(false);
    setFormError('');
  };

  const handleSave = (e) => {
    e.preventDefault();
    setFormError('');
    const eid = eventId(item);
    if (!eid) return;
    if (!formTitle.trim()) {
      setFormError('Title is required.');
      return;
    }
    if (!formEventDate || !/^\d{4}-\d{2}-\d{2}$/.test(formEventDate)) {
      setFormError('Event date is required (use a valid date).');
      return;
    }
    const nextTitle = formTitle.trim();
    const nextDesc = formDescription;
    const nextDate = formEventDate;
    const prevTitle = (item.title ?? '').trim();
    const prevDesc = eventDescription(item);
    const prevDate = eventDateInputValue(item);
    if (nextTitle === prevTitle && nextDesc === prevDesc && nextDate === prevDate) {
      setFormError('Change the title, description, or date before saving.');
      return;
    }

    setFormSaving(true);
    eventsApi
      .update(eid, {
        title: nextTitle,
        description: nextDesc,
        event_date: nextDate,
      })
      .then(() => loadOne({ silent: true }))
      .then(() => closeEdit())
      .catch((err) => setFormError(err.userMessage || 'Failed to save.'))
      .finally(() => setFormSaving(false));
  };

  const handleDelete = () => {
    const eid = eventId(item);
    if (!eid) return;
    setDeleting(true);
    eventsApi
      .delete(eid)
      .then(() => navigate('/admin/events', { replace: true }))
      .catch((err) => {
        setError(err.userMessage || 'Failed to delete');
        setDeleteOpen(false);
      })
      .finally(() => setDeleting(false));
  };

  const formatDate = (str) => {
    if (!str) return '—';
    try {
      const d = new Date(str);
      return Number.isNaN(d.getTime())
        ? str
        : d.toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' });
    } catch {
      return str;
    }
  };

  if (loading) {
    return <div className="ev-view-loading">Loading event…</div>;
  }

  if (error && !item) {
    return (
      <div className="ev-view-wrap">
        <div className="ev-view-header">
          <Link to="/admin/events" className="ev-view-back">
            ← Events
          </Link>
        </div>
        <div className="ev-view-error" role="alert">
          {error}
        </div>
      </div>
    );
  }

  const eid = eventId(item);
  const body = eventDescription(item);

  return (
    <div className="ev-view-wrap">
      <div className="ev-view-header">
        <Link to="/admin/events" className="ev-view-back">
          ← Events
        </Link>
        <h2 className="ev-view-title">{item?.title || 'Event'}</h2>
        <div className="ev-view-actions">
          <button type="button" className="ev-view-btn ev-view-btn-edit" onClick={openEdit} disabled={!eid}>
            Edit event
          </button>
          <button type="button" className="ev-view-btn ev-view-btn-delete-outline" onClick={() => setDeleteOpen(true)}>
            Delete
          </button>
        </div>
      </div>

      {error && (
        <div className="ev-view-error ev-view-error--inline" role="alert">
          {error}
        </div>
      )}

      <div className="ev-view-card">
        <dl className="ev-view-meta">
          <div className="ev-view-row">
            <dt>Event date</dt>
            <dd>{formatEventDateShort(eventDateInputValue(item))}</dd>
          </div>
          <div className="ev-view-row">
            <dt>Created</dt>
            <dd>{formatDate(item?.created_at)}</dd>
          </div>
          {item?.updated_at && item.updated_at !== item.created_at && (
            <div className="ev-view-row">
              <dt>Updated</dt>
              <dd>{formatDate(item.updated_at)}</dd>
            </div>
          )}
          {item?.creator?.email && (
            <div className="ev-view-row">
              <dt>Author</dt>
              <dd>
                {item.creator.name ? `${item.creator.name} · ${item.creator.email}` : item.creator.email}
              </dd>
            </div>
          )}
        </dl>
        <div className="ev-view-body">
          <h3 className="ev-view-body-heading">Description</h3>
          {body ? (
            <div className="ev-view-body-text">{body}</div>
          ) : (
            <p className="ev-view-body-empty">No description.</p>
          )}
        </div>
      </div>

      <Modal
        isOpen={editOpen}
        onRequestClose={() => {
          if (!formSaving) closeEdit();
        }}
        contentLabel="Edit event"
        className="ev-view-modal-content"
        overlayClassName="ev-view-modal-overlay"
      >
        <div className="ev-view-modal-inner">
          <h3 id="ev-view-edit-title">Edit event</h3>
          <form onSubmit={handleSave}>
            {formError && (
              <div className="ev-view-form-error" role="alert">
                {formError}
              </div>
            )}
            <div className="ev-view-field">
              <label htmlFor="ev-view-title">Title *</label>
              <input
                id="ev-view-title"
                type="text"
                value={formTitle}
                onChange={(e) => setFormTitle(e.target.value)}
                disabled={formSaving}
              />
            </div>
            <div className="ev-view-field">
              <label htmlFor="ev-view-description">Description</label>
              <textarea
                id="ev-view-description"
                value={formDescription}
                onChange={(e) => setFormDescription(e.target.value)}
                rows={6}
                disabled={formSaving}
              />
            </div>
            <div className="ev-view-field">
              <label htmlFor="ev-view-date">Event date *</label>
              <input
                id="ev-view-date"
                type="date"
                value={formEventDate}
                onChange={(e) => setFormEventDate(e.target.value)}
                disabled={formSaving}
              />
            </div>
            <div className="ev-view-modal-actions">
              <button type="button" className="ev-view-modal-cancel" onClick={closeEdit} disabled={formSaving}>
                Cancel
              </button>
              <button type="submit" className="ev-view-btn ev-view-btn-submit" disabled={formSaving}>
                {formSaving ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </form>
        </div>
      </Modal>

      {deleteOpen && (
        <div className="ev-view-delete-modal" role="dialog" aria-modal="true" aria-labelledby="ev-view-del-title">
          <div className="ev-view-delete-backdrop" onClick={() => !deleting && setDeleteOpen(false)} aria-hidden />
          <div className="ev-view-delete-box">
            <h3 id="ev-view-del-title">Delete this event?</h3>
            <p>This soft-deletes the event. This cannot be undone from the admin UI.</p>
            <div className="ev-view-delete-actions">
              <button
                type="button"
                className="ev-view-btn ev-view-btn-muted"
                onClick={() => setDeleteOpen(false)}
                disabled={deleting}
              >
                Cancel
              </button>
              <button type="button" className="ev-view-btn ev-view-btn-danger" onClick={handleDelete} disabled={deleting}>
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default EventView;
